import React, { useState, useEffect } from 'react';
import { apiService, handleApiError } from '../services/api';
import { useMeeting } from '../contexts/MeetingContext';
import {
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  BuildingOfficeIcon,
  LightBulbIcon,
  BeakerIcon,
  ChartBarIcon,
  SparklesIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import PDFDocumentViewer from './PDFDocumentViewer';

const StudyItemOverview = () => {
  const { selectedMeeting, selectedAgenda } = useMeeting();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentViewer, setDocumentViewer] = useState({
    isOpen: false,
    documentId: null,
    verbatimProposals: [],
    subtopicName: null,
    semanticGroupName: null
  });

  useEffect(() => {
    loadContext();
  }, [selectedMeeting, selectedAgenda]);

  const loadContext = async () => {
    try {
      setLoading(true);
      setError(null);
      setContext(null);
      const data = await apiService.getAgendaContext(selectedMeeting, selectedAgenda);
      setContext(data);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      setContext(null); // Clear context on error
    } finally {
      setLoading(false);
    }
  };

  const handleViewPDF = (tdocId, description) => {
    console.log('📄 Opening PDF viewer for document:', tdocId);
    setDocumentViewer({
      isOpen: true,
      documentId: tdocId,
      verbatimProposals: [], // No proposals for administrative documents
      subtopicName: description || `Administrative Document: ${tdocId}`,
      semanticGroupName: null
    });
  };

  const closeDocumentViewer = () => {
    setDocumentViewer({
      isOpen: false,
      documentId: null,
      verbatimProposals: [],
      subtopicName: null,
      semanticGroupName: null
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  // If there's an error (no context generated), show blank screen instead of error message
  if (error) {
    return <div className="min-h-screen"></div>;
  }

  return (
    <div className="space-y-4 animate-fadeIn">
      {context && (
        <>
          {/* Check if this is an administrative agenda */}
          {context.is_administrative && context.administrative_summary ? (
            // Render Administrative Agenda
            <>
              {/* Header */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <div className="mb-4">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Administrative Agenda: {selectedAgenda}
                    {context.agenda_description && ` - ${context.agenda_description}`}
                  </h1>
                  <p className="text-lg text-gray-600 font-medium">
                    {context.administrative_summary.one_line_summary}
                  </p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                  <div className="text-2xl font-bold text-red-600">{context.administrative_summary.quick_stats.tr_count || 0}</div>
                  <div className="text-sm text-gray-500">Technical Reports</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                  <div className="text-2xl font-bold text-red-600">
                    {context.administrative_summary.quick_stats.ls_in || 0} / {context.administrative_summary.quick_stats.ls_out || 0}
                  </div>
                  <div className="text-sm text-gray-500">LSs (In / Out)</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                  <div className="text-2xl font-bold text-red-600">{context.administrative_summary.quick_stats.wi_active || 0}</div>
                  <div className="text-sm text-gray-500">Work Items</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                  <div className="text-2xl font-bold text-red-600">
                    {context.administrative_summary.quick_stats.actions_pending || 0}
                  </div>
                  <div className="text-sm text-gray-500">Actions Pending</div>
                </div>
              </div>

              {/* Main Content - 3 Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left Column: Liaison Statements */}
                {context.administrative_summary.ls_summary && (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden h-fit">
                    <div className="bg-red-600 px-4 py-3">
                      <h2 className="text-base font-bold text-white">Liaison Statements</h2>
                    </div>
                    <div className="p-4">
                      {/* Incoming LSs */}
                      {context.administrative_summary.ls_summary.incoming && context.administrative_summary.ls_summary.incoming.length > 0 && (
                        <div className="mb-4 pb-4 border-b-2 border-gray-300">
                          <h3 className="font-semibold text-gray-900 mb-2.5 text-base">Incoming ({context.administrative_summary.ls_summary.incoming.length})</h3>
                          {context.administrative_summary.ls_summary.incoming.map((ls, index) => (
                            <div key={index} className="mb-3 pb-3 border-b border-gray-100 last:border-0">
                              <div className="flex items-start justify-between mb-1.5">
                                <p className="text-[15px] font-medium text-gray-900 leading-snug">{ls.topic}</p>
                                <button
                                  onClick={() => handleViewPDF(ls.tdoc, `Liaison Statement: ${ls.topic}\n\nFrom: ${ls.from}\n\n${ls.context || ''}`)}
                                  className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors flex-shrink-0 ml-2"
                                  title="View PDF"
                                >
                                  <EyeIcon className="h-3 w-3" />
                                  {ls.tdoc}
                                </button>
                              </div>
                              <p className="text-[13px] text-gray-500">From: {ls.from}</p>
                              {ls.context && <p className="text-[13px] text-gray-600 mt-1.5 leading-relaxed">{ls.context}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Outgoing LSs */}
                      {context.administrative_summary.ls_summary.outgoing && context.administrative_summary.ls_summary.outgoing.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2.5 text-base">Outgoing ({context.administrative_summary.ls_summary.outgoing.length})</h3>
                          {context.administrative_summary.ls_summary.outgoing.map((ls, index) => (
                            <div key={index} className="mb-3 pb-3 border-b border-gray-100 last:border-0">
                              <div className="flex items-start justify-between mb-1.5">
                                <p className="text-[15px] font-medium text-gray-900 leading-snug">{ls.topic}</p>
                                <button
                                  onClick={() => handleViewPDF(ls.tdoc, `Liaison Statement: ${ls.topic}\n\nTo: ${ls.to}\n\n${ls.context || ''}`)}
                                  className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors flex-shrink-0 ml-2"
                                  title="View PDF"
                                >
                                  <EyeIcon className="h-3 w-3" />
                                  {ls.tdoc}
                                </button>
                              </div>
                              <p className="text-[13px] text-gray-500">To: {ls.to}</p>
                              {ls.context && <p className="text-[13px] text-gray-600 mt-1.5 leading-relaxed">{ls.context}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Center Column: Technical Reports, Work Items, Glossary (Stacked) */}
                <div className="space-y-4">
                  {/* Technical Reports */}
                  {context.administrative_summary.tr_status && context.administrative_summary.tr_status.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                      <div className="bg-red-600 px-4 py-3">
                        <h2 className="text-base font-bold text-white">Technical Reports</h2>
                      </div>
                      <div className="p-4">
                        {context.administrative_summary.tr_status.map((tr, index) => (
                          <div key={index} className="mb-2.5 pb-2.5 border-b border-gray-200 last:border-0 last:mb-0 last:pb-0">
                            <div className="flex items-start justify-between mb-1.5">
                              <div>
                                <h3 className="font-semibold text-gray-900 text-[15px]">{tr.spec}</h3>
                                <p className="text-[14px] text-gray-600">Version: {tr.current_version} • Status: {tr.status}</p>
                                {tr.release && <p className="text-[13px] text-gray-500">{tr.release}</p>}
                              </div>
                            </div>
                            {tr.context && <p className="text-[14px] text-gray-600 mt-1.5 leading-relaxed">{tr.context}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Work Items */}
                  {context.administrative_summary.work_items && context.administrative_summary.work_items.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                      <div className="bg-red-600 px-4 py-3">
                        <h2 className="text-base font-bold text-white">Work Items ({context.administrative_summary.work_items.length})</h2>
                      </div>
                      <div className="p-4">
                        {context.administrative_summary.work_items.map((wi, index) => (
                          <div key={index} className="mb-2.5 pb-2.5 border-b border-gray-200 last:border-0 last:mb-0 last:pb-0">
                            <div className="flex items-start justify-between mb-1.5">
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-900 text-[15px]">{wi.name}</h3>
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[14px] text-gray-600 mt-0.5">
                                  <span>{wi.release}</span>
                                  <span>•</span>
                                  <span>Type: {wi.type}</span>
                                  {wi.status && (
                                    <>
                                      <span>•</span>
                                      <span className="text-green-700">Status: {wi.status}</span>
                                    </>
                                  )}
                                </div>
                                {wi.timeline && <p className="text-[13px] text-gray-500 mt-0.5">Timeline: {wi.timeline}</p>}
                              </div>
                            </div>
                            {wi.scope && <p className="text-[14px] text-gray-600 mt-1.5 leading-relaxed">{wi.scope}</p>}
                            {wi.deliverables && wi.deliverables.length > 0 && (
                              <p className="text-[13px] text-gray-500 mt-1.5">
                                Deliverables: {wi.deliverables.join(', ')}
                              </p>
                            )}
                            {wi.context && <p className="text-[13px] text-gray-500 mt-1.5 italic">{wi.context}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Glossary */}
                  {context.administrative_summary.glossary && Object.keys(context.administrative_summary.glossary).length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                      <div className="bg-gray-600 px-4 py-3">
                        <h2 className="text-base font-bold text-white">Glossary</h2>
                      </div>
                      <div className="p-4">
                        <dl className="space-y-2">
                          {Object.entries(context.administrative_summary.glossary).map(([term, definition]) => (
                            <div key={term} className="text-[14px] leading-relaxed">
                              <dt className="font-semibold text-gray-900 inline">{term}:</dt>
                              <dd className="text-gray-600 inline ml-2">{definition}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    </div>
                  )}
                  
                  {/* Other Documents */}
                  {context.administrative_summary.documents && context.administrative_summary.documents.length > 0 && (
                    <div className="mt-4 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                      <div className="bg-charcoal-900 px-4 py-3">
                        <h2 className="text-base font-bold text-white">Other Documents ({context.administrative_summary.documents.length})</h2>
                      </div>
                      <div className="p-4">
                        <div className="space-y-3">
                          {context.administrative_summary.documents.map((doc, index) => (
                            <div key={index} className="flex items-start justify-between border-b border-gray-200 last:border-0 pb-3 last:pb-0">
                              <div className="flex-1">
                                <h3 className="text-[14px] font-semibold text-gray-900 leading-snug">{doc.title}</h3>
                                <p className="text-[13px] text-gray-600 mt-0.5">{doc.source}</p>
                              </div>
                              {doc.tdoc_id && (
                                <button
                                  onClick={() => handleViewPDF(doc.tdoc_id, doc.title)}
                                  className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors flex-shrink-0 ml-2"
                                  title="View PDF"
                                >
                                  <EyeIcon className="h-3 w-3" />
                                  {doc.tdoc_id}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column: Actions Required */}
                {context.administrative_summary.actions_tracker && context.administrative_summary.actions_tracker.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden h-fit">
                    <div className="bg-red-600 px-4 py-3">
                      <h2 className="text-base font-bold text-white">Actions Required ({context.administrative_summary.actions_tracker.length})</h2>
                    </div>
                    <div className="p-4">
                      <div className="space-y-2.5">
                        {context.administrative_summary.actions_tracker.map((action, index) => (
                          <div key={index} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-start justify-between mb-1.5">
                              <div className="flex-1">
                                <p className="text-[14px] font-medium text-gray-900 leading-snug">{action.action}</p>
                                <p className="text-[13px] text-gray-600 mt-1">Owner: {action.owner}{action.deadline && ` • Deadline: ${action.deadline}`}</p>
                              </div>
                              <div className="flex flex-col items-end gap-1 ml-3">
                                {action.priority && (
                                  <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
                                    action.priority === 'high' ? 'bg-red-100 text-red-700' :
                                    action.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {action.priority}
                                  </span>
                                )}
                                {action.status && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 flex-shrink-0">
                                    {action.status}
                                  </span>
                                )}
                              </div>
                            </div>
                            {action.context && (
                              <p className="text-[13px] text-gray-600 mt-1.5 italic leading-relaxed">{action.context}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Render Regular Proposal-Based Agenda
            <>
              {/* Header */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-3xl font-bold text-gray-900">
                    Agenda Context: {selectedAgenda}
                    {context.agenda_description && ` - ${context.agenda_description}`}
                  </h1>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{context.subtopic_count}</div>
                      <div className="text-sm text-gray-500">subtopics</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{context.proposal_count}</div>
                      <div className="text-sm text-gray-500">proposals</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{context.document_count}</div>
                      <div className="text-sm text-gray-500">documents</div>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 text-base leading-relaxed">{context.overview}</p>
              </div>

          {/* Four Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Key Technical Themes */}
            {context.key_themes && context.key_themes.themes && context.key_themes.themes.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-red-600 px-4 py-3 border-b border-red-700">
                  <h2 className="text-base font-bold text-white">Key Technical Themes</h2>
                </div>
                <div className="p-4">
                  <ul className="space-y-2.5">
                    {context.key_themes.themes.map((theme, index) => (
                      <li key={index} className="flex items-start text-base">
                        <span className="text-red-600 mr-2">•</span>
                        <span className="text-gray-700">{theme}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Contributing Companies */}
            {context.company_landscape && context.company_landscape.top_companies && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-red-600 px-4 py-3 border-b border-red-700">
                  <h2 className="text-base font-bold text-white">Contributing Companies</h2>
                </div>
                <div className="p-4">
                  {context.company_landscape.summary && (
                    <p className="text-base text-gray-600 mb-3 pb-3 border-b border-gray-200">{context.company_landscape.summary}</p>
                  )}
                  <div className="space-y-2.5">
                    {context.company_landscape.top_companies.map((company, index) => (
                      <div key={index} className="flex items-center justify-between py-1 px-2 rounded hover:bg-gray-50">
                        <span className="text-gray-700 text-base">{company.company}</span>
                        <span className="text-sm bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">
                          {company.proposal_count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Technical Scope */}
            {context.technical_scope && context.technical_scope.scope && context.technical_scope.scope.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-red-600 px-4 py-3 border-b border-red-700">
                  <h2 className="text-base font-bold text-white">Technical Scope</h2>
                </div>
                <div className="p-4">
                  <ul className="space-y-2.5">
                    {context.technical_scope.scope.map((item, index) => (
                      <li key={index} className="flex items-start text-base">
                        <span className="text-red-600 mr-2">•</span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Documents Section (for CR-based agendas) */}
            {context.technical_scope && context.technical_scope.documents && context.technical_scope.documents.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-red-600 px-4 py-3 border-b border-red-700">
                  <h2 className="text-base font-bold text-white">Documents</h2>
                </div>
                <div className="p-4">
                  <div className="space-y-4">
                    {context.technical_scope.documents.map((doc, index) => (
                      <div key={index} className="border-b border-gray-200 last:border-0 pb-4 last:pb-0">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 text-base">{doc.title}</h3>
                            <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                              <span className="font-medium">{doc.company}</span>
                              {doc.spec && (
                                <>
                                  <span>•</span>
                                  <span className="text-red-600">{doc.spec}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleViewPDF(doc.tdoc, `CR: ${doc.title}\n\nCompany: ${doc.company}${doc.spec ? `\nSpec: ${doc.spec}` : ''}\n\n${doc.summary}`)}
                            className="flex items-center gap-2 text-sm bg-red-100 text-red-700 px-3 py-1.5 rounded hover:bg-red-200 transition-colors flex-shrink-0 ml-3"
                            title="View PDF"
                          >
                            <EyeIcon className="h-4 w-4" />
                            {doc.tdoc}
                          </button>
                        </div>
                        {doc.summary && (
                          <p className="text-sm text-gray-700 leading-relaxed mt-2">{doc.summary}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
            </>
          )}
        </>
      )}

      {/* Document Viewer Modal */}
      {documentViewer.isOpen && (
        <PDFDocumentViewer
          documentId={documentViewer.documentId}
          isOpen={documentViewer.isOpen}
          onClose={closeDocumentViewer}
          verbatimProposals={documentViewer.verbatimProposals}
          subtopicName={documentViewer.subtopicName}
          semanticGroupName={documentViewer.semanticGroupName}
        />
      )}
    </div>
  );
};

export default StudyItemOverview;
